import {
  isChartedTreatmentGroup,
  isTreatmentPlan,
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedTreatment,
  type IChartedTreatmentGroup,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import { DragDropGroup, type IDragDropNode } from './drag-drop-group';
import {
  type CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { isSameRef, isWithRef, type WithRef } from '@principle-theorem/shared';

export interface INestedItemRemoved {
  item: IChartedTreatment;
  sourceContainerId: string;
}

export interface ITreatmentDropListParent {
  plan?: WithRef<ITreatmentPlan> | IChartedMultiStepTreatment;
  step?: WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep;
}

export type TreatmentParent = ITreatmentDropListParent | undefined;
export type TreatmentItem = IChartedTreatment | IChartedTreatmentGroup;
export type ITreatmentDragDropNode = IDragDropNode<
  TreatmentParent,
  TreatmentItem
>;

export class TreatmentDragDropGroup extends DragDropGroup<
  TreatmentParent,
  TreatmentItem
> {
  private _nestedItemRemoved$ = new Subject<INestedItemRemoved>();
  nestedItemRemoved$ = this._nestedItemRemoved$.asObservable();

  override drop(event: CdkDragDrop<ITreatmentDragDropNode>): void {
    const draggedItem = event.item.data as TreatmentItem;
    const sourceItems = event.previousContainer.data?.items;
    const targetItems = event.container.data?.items;
    if (!sourceItems || !targetItems) {
      return;
    }
    const isFromNestedList = event.previousContainer.id?.startsWith('nested-');
    const isToNestedList = event.container.id?.startsWith('nested-');
    const actualPreviousIndex = sourceItems.findIndex(
      (item) => item.uuid === draggedItem.uuid
    );

    // Moving from nested list to parent list
    if (isFromNestedList && !isToNestedList) {
      if (this._treatmentExistsInList(targetItems, draggedItem.uuid)) {
        return;
      }

      targetItems.splice(event.currentIndex, 0, draggedItem);

      this._nestedItemRemoved$.next({
        item: draggedItem as IChartedTreatment,
        sourceContainerId: event.previousContainer.id,
      });

      this._afterDrop$.next({
        previous: event.previousContainer,
        current: event.container,
        item: draggedItem,
      });
      return;
    }

    if (actualPreviousIndex === -1) {
      return;
    }

    if (event.previousContainer === event.container) {
      moveItemInArray(targetItems, actualPreviousIndex, event.currentIndex);
      this._afterDrop$.next({
        previous: event.previousContainer,
        current: event.container,
        item: draggedItem,
      });
      return;
    }

    if (this._treatmentExistsInList(targetItems, draggedItem.uuid)) {
      return;
    }

    transferArrayItem(
      sourceItems,
      targetItems,
      actualPreviousIndex,
      event.currentIndex
    );
    this._afterDrop$.next({
      previous: event.previousContainer,
      current: event.container,
      item: draggedItem,
    });
  }

  private _treatmentExistsInList(
    items: TreatmentItem[],
    uuid: string
  ): boolean {
    return items.some((item) => {
      if (item.uuid === uuid) {
        return true;
      }
      if (isChartedTreatmentGroup(item)) {
        return item.treatments.some((treatment) => treatment.uuid === uuid);
      }
      return false;
    });
  }
}

export function isCrossPlanTreatmentMove(
  previousParent: TreatmentParent,
  currentParent: TreatmentParent
): previousParent is ITreatmentDropListParent & {
  plan: WithRef<ITreatmentPlan>;
  step: WithRef<ITreatmentStep>;
} {
  const previousPlan = previousParent?.plan;
  const currentPlan = currentParent?.plan;
  const previousStep = previousParent?.step;
  const currentStep = currentParent?.step;

  if (
    !previousPlan ||
    !currentPlan ||
    !previousStep ||
    !currentStep ||
    !isTreatmentPlan(previousPlan) ||
    !isTreatmentPlan(currentPlan) ||
    !isWithRef(previousStep) ||
    !isWithRef(currentStep)
  ) {
    return false;
  }

  return !isSameRef(previousPlan.ref, currentPlan.ref);
}
